/**
 * Can this browser, on this device, in this display mode, receive a push at all?
 *
 * Four different "no"s hide behind that question and they need four different answers on screen.
 * A desktop Firefox with notifications blocked needs to be sent to site settings; an iPhone in a
 * Safari tab needs to install the app first and nothing else will do; an old iPhone can only be
 * told the truth. Collapsing them into one «недоступно» leaves the largest group — iOS in a tab —
 * with a dead end they could have walked out of in two taps.
 *
 * Kept as a pure function over an explicit environment so all five outcomes are testable. A test
 * runner cannot pretend to be an iPad in standalone mode with notifications already denied.
 */
import { isIosLike, isStandalone } from '@/features/pwa/platform';

export type PushAvailability =
  /** Everything is in place and permission has not been asked for yet. */
  | 'ready'
  /** Already granted — the token just needs to exist and be registered. */
  | 'granted'
  /** Refused. Cannot be re-asked from JS; only the person can undo it in site settings. */
  | 'denied'
  /** iOS-family device in a browser tab: web push exists there only once installed. */
  | 'needs-install'
  /** No push API on this browser at all. */
  | 'unsupported';

export interface PushEnvironment {
  hasNotification: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  /** `null` when the Notification API is absent, so there is nothing to read it from. */
  permission: NotificationPermission | null;
  iosLike: boolean;
  standalone: boolean;
}

export function pushAvailability(env: PushEnvironment): PushAvailability {
  // Deliberately ahead of the capability checks. On iOS the APIs are missing *because* the app
  // is in a tab, and reporting 'unsupported' would tell an iPhone 15 on iOS 18 that its browser
  // cannot do this — when in fact it can, one «На экран „Домой“» away. Getting the order wrong
  // here is the difference between an instruction and a brick wall.
  if (env.iosLike && !env.standalone) return 'needs-install';

  if (!env.hasNotification || !env.hasServiceWorker || !env.hasPushManager) return 'unsupported';
  if (env.permission === 'denied') return 'denied';
  if (env.permission === 'granted') return 'granted';
  return 'ready';
}

/** Reads the live environment. Everything it looks at is absent in a worker or during SSR. */
export function readPushEnvironment(): PushEnvironment {
  const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
  return {
    hasNotification,
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    permission: hasNotification ? Notification.permission : null,
    iosLike:
      typeof navigator !== 'undefined' &&
      isIosLike(navigator.userAgent, navigator.maxTouchPoints ?? 0),
    standalone: isStandalone(),
  };
}

export function currentPushAvailability(): PushAvailability {
  return pushAvailability(readPushEnvironment());
}
